// XLSX export — builds an Excel task list (ID, Duration, Start, Finish, Predecessors, ...)
// for import into an external planning tool (e.g. MS Project's Excel task-mapping import).
// Write-only: this module never parses an .xlsx file, so the known SheetJS parser CVEs
// (prototype pollution / ReDoS, both in the read path) are not exercised here.
import * as XLSX from 'xlsx';
import type { Box, Arrow } from '../types';

export interface TaskExportRow {
  id: number;
  name: string;
  duration: string;
  start: Date;
  finish: Date;
  percentComplete: number;
  predecessors: string;
  resourceNames: string;
}

// MS Project's Excel import recognizes spelled-out duration units ("5 days") more reliably
// than the app's own short-hand ("5d") across locales, so translate rather than pass through.
function formatDurationForExport(duration: string): string {
  const m = (duration || '').match(/^(\d+(?:\.\d+)?)\s*([hdwHDW]?)$/);
  if (!m) return duration || '';
  const n = m[1];
  const unit = (m[2] || 'd').toLowerCase();
  const word = unit === 'h' ? 'hrs' : unit === 'w' ? 'wks' : 'days';
  return `${n} ${word}`;
}

export function buildTaskExportRows(
  boxes: Box[],
  arrows: Arrow[],
  getStartDate: (id: number) => Date,
  getEndDate: (id: number) => Date,
): TaskExportRow[] {
  // startday markers and callouts aren't project tasks — same exclusion the Gantt view uses.
  const tasks = boxes.filter(b => b.type !== 'callout' && b.type !== 'startday');
  const calloutIds = new Set(boxes.filter(b => b.type === 'callout').map(b => b.id));
  return tasks.map(b => {
    const predecessors = arrows
      .filter(a => (a.toBox ?? a.to) === b.id && !calloutIds.has((a.fromBox ?? a.from) as number))
      .map(a => a.fromBox ?? a.from)
      .filter((id): id is number => id !== undefined && id !== null)
      .join(', ');
    return {
      id: b.id,
      name: b.text || '',
      duration: formatDurationForExport(b.duration),
      start: getStartDate(b.id),
      finish: getEndDate(b.id),
      percentComplete: b.completion || 0,
      predecessors,
      resourceNames: b.assignee || '',
    };
  });
}

export function exportTasksToXlsx(rows: TaskExportRow[], filename: string): void {
  const data = rows.map(r => ({
    ID: r.id,
    'Task Name': r.name,
    Duration: r.duration,
    Start: r.start,
    Finish: r.finish,
    '% Complete': r.percentComplete,
    Predecessors: r.predecessors,
    'Resource Names': r.resourceNames,
  }));
  const ws = XLSX.utils.json_to_sheet(data, { cellDates: true });
  ws['!cols'] = [
    { wch: 6 }, { wch: 40 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 11 }, { wch: 14 }, { wch: 20 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Tasks');
  XLSX.writeFile(wb, `${filename}.xlsx`, { cellDates: true });
}
